import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PostCard from "./PostCard";

export default function PostFeedList({ userId, pool = "private", reloadKey }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const sentinel = useRef(null);

  const fetchMore = useCallback(async (reset = false) => {
    if (loading) return;
    if (!reset && done) return;
    setLoading(true);
    try {
      const params = { limit: 20, pool };
      if (!reset && cursor) params.cursor = cursor;
      const r = await api.get(`/profile-posts/user/${userId}`, { params });
      const newItems = r.data?.items || [];
      setItems((prev) => reset ? newItems : [...prev, ...newItems]);
      setCursor(r.data?.next_cursor || null);
      setDone(!r.data?.next_cursor);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, pool, cursor, loading, done]);

  useEffect(() => {
    setItems([]); setCursor(null); setDone(false);
    fetchMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reloadKey]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !done) fetchMore();
    }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchMore, loading, done]);

  return (
    <div data-testid="post-feed-list">
      {items.map((p) => (
        <PostCard key={p.id} post={p} pinnable onDeleted={() => fetchMore(true)} onPinned={() => fetchMore(true)} />
      ))}
      {items.length === 0 && !loading && (
        <div className="text-center py-10 text-[var(--cream-faint)] font-meta text-sm">
          {t("post.empty", "Ingen poster ennå.")}
        </div>
      )}
      {loading && (
        <div className="text-center py-4 text-[var(--cream-faint)] font-meta text-xs uppercase tracking-wider">
          {t("post.loading", "Laster …")}
        </div>
      )}
      <div ref={sentinel} aria-hidden style={{ height: 1 }} />
    </div>
  );
}
